import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

import { useApiClient } from '../../hooks/useApiClient';
import type { DeviceProvisionResponse, DeviceSummary, DeviceTokenSummary } from '../../types';

interface CreateDeviceForm {
  name: string;
  label: string;
  description: string;
}

const INITIAL_FORM: CreateDeviceForm = {
  name: '',
  label: '',
  description: '',
};

const filenameSafe = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'token';

export default function DevicesPage() {
  const apiClient = useApiClient();
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateDeviceForm>(INITIAL_FORM);
  const [issuedSecrets, setIssuedSecrets] = useState<Record<string, { token: string; qrPayload: string }>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedDevice, setExpandedDevice] = useState<string | null>(null);
  const handleDownloadQr = useCallback((tokenId: string, label: string, prefix: string) => {
    const canvas = document.getElementById(`device-qr-${tokenId}`) as HTMLCanvasElement | null;
    if (!canvas) {
      toast.error('QR code not ready');
      return;
    }
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `device-${filenameSafe(label)}-${prefix}.png`;
      link.click();
    } catch (error) {
      console.error(error);
      toast.error('Unable to download QR code');
    }
  }, []);

  const loadDevices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient('/api/admin/devices');
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const payload = (await response.json()) as DeviceSummary[];
      setDevices(payload);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Unable to load devices from the backend.');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  const sortedDevices = useMemo(
    () => [...devices].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [devices],
  );

  const handleCreateDevice = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.label.trim()) {
      toast.error('Name and label are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient('/api/admin/devices', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          label: form.label.trim(),
          description: form.description.trim() || undefined,
        }),
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || 'Unable to create device');
      }
      const payload = (await response.json()) as DeviceProvisionResponse;
      setDevices((prev) => [...prev, payload.device]);
      setIssuedSecrets((prev) => ({
        ...prev,
        [payload.tokenInfo.id]: {
          token: payload.token,
          qrPayload: payload.tokenInfo.qrPayload ?? '',
        },
      }));
      setExpandedDevice(payload.device.id);
      toast.success(`Added device ${payload.device.label}. Copy the token now—it's shown only once.`);
      setForm(INITIAL_FORM);
    } catch (submitError) {
      console.error(submitError);
      toast.error(submitError instanceof Error ? submitError.message : 'Failed to create device');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleDevice = async (device: DeviceSummary) => {
    try {
      const response = await apiClient(`/api/admin/devices/${device.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !device.isActive }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const updated = (await response.json()) as DeviceSummary;
      setDevices((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(`${updated.label} ${updated.isActive ? 'enabled' : 'disabled'}`);
    } catch (toggleError) {
      console.error(toggleError);
      toast.error('Unable to update device status');
    }
  };

  const handleDeleteDevice = async (device: DeviceSummary) => {
    const confirmed = window.confirm(`Remove device "${device.label}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }
    try {
      const response = await apiClient(`/api/admin/devices/${device.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      setDevices((prev) => prev.filter((item) => item.id !== device.id));
      setIssuedSecrets((prev) => {
        const next = { ...prev };
        device.tokens.forEach((token) => {
          delete next[token.id];
        });
        return next;
      });
      toast.success(`Removed device ${device.label}`);
    } catch (deleteError) {
      console.error(deleteError);
      toast.error('Unable to delete device');
    }
  };

  const handleRevokeToken = async (token: DeviceTokenSummary) => {
    try {
      const response = await apiClient(`/api/admin/device-tokens/${token.id}/revoke`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      toast.success('Token revoked');
      await loadDevices();
    } catch (revokeError) {
      console.error(revokeError);
      toast.error('Unable to revoke token');
    }
  };

  const handleExtendToken = async (token: DeviceTokenSummary) => {
    try {
      const response = await apiClient(`/api/admin/device-tokens/${token.id}/extend`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const updated = (await response.json()) as DeviceTokenSummary;
      toast.success('Token extended by 1 year');
      await loadDevices();
    } catch (extendError) {
      console.error(extendError);
      toast.error('Unable to extend token');
    }
  };

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success('Token copied to clipboard');
    } catch (copyError) {
      console.warn(copyError);
      toast.error('Unable to copy token');
    }
  };

  return (
    <div className="admin-section">
      <header className="admin-section-header">
        <div>
          <h1>Devices</h1>
          <p>Track M400 headsets and manage their authentication tokens.</p>
        </div>
      </header>

      <form className="admin-form" onSubmit={handleCreateDevice}>
        <h2>Register Device</h2>
        <div className="admin-form-grid">
          <label>
            <span>Unique Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="m400-alpha"
              required
              disabled={isSubmitting}
            />
          </label>
          <label>
            <span>Label</span>
            <input
              type="text"
              value={form.label}
              onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
              placeholder="Endoscopic headset"
              required
              disabled={isSubmitting}
            />
          </label>
        </div>
        <label>
          <span>Description</span>
          <textarea
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            placeholder="Optional notes about deployment"
            disabled={isSubmitting}
          />
        </label>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Add Device'}
        </button>
      </form>

      <section className="admin-list">
        <h2>Registered Devices</h2>
        {isLoading ? <p>Loading devices…</p> : null}
        {error ? <p className="admin-error">{error}</p> : null}
        {!isLoading && !error ? (
          sortedDevices.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '760px',
                }}
              >
                <thead>
                  <tr style={{ textAlign: 'left', color: '#cbd5f5' }}>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Label</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Identifier</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Status</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Created</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDevices.map((device) => {
                    const isExpanded = expandedDevice === device.id;
                    const tokens = device.tokens ?? [];
                    return (
                      <Fragment key={device.id}>
                        <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                          <td style={{ padding: '0.85rem 1rem', color: '#e2e8f0', fontWeight: 600 }}>{device.label}</td>
                          <td style={{ padding: '0.85rem 1rem', color: '#94a3b8' }}>{device.name}</td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <span className={device.isActive ? 'badge badge-success' : 'badge badge-neutral'}>
                              {device.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '0.85rem 1rem', color: '#94a3b8' }}>
                            {new Date(device.createdAt).toLocaleString()}
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => handleToggleDevice(device)}>
                                {device.isActive ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteDevice(device)}
                                style={{
                                  border: '1px solid rgba(248, 113, 113, 0.4)',
                                  background: 'rgba(248, 113, 113, 0.15)',
                                  color: '#fecaca',
                                }}
                              >
                                Delete
                              </button>
                              <button
                                type="button"
                                onClick={() => setExpandedDevice(isExpanded ? null : device.id)}
                              >
                                {isExpanded ? 'Hide Details' : 'Show Details'}
                              </button>
                            </div>
                            <span style={{ display: 'none' }}>
                              {tokens.map((token) => {
                                const qrValue =
                                  issuedSecrets[token.id]?.qrPayload ?? token.qrPayload ?? '';
                                return (
                                  <QRCodeCanvas
                                    key={token.id}
                                    value={qrValue || ''}
                                    size={132}
                                    includeMargin
                                    id={`device-qr-${token.id}`}
                                  />
                                );
                              })}
                            </span>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={5} style={{ padding: '1rem 1.25rem', background: 'rgba(15,23,42,0.65)' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', color: '#cbd5f5' }}>
                                {device.description ? (
                                  <p style={{ margin: 0, color: '#94a3b8' }}>{device.description}</p>
                                ) : null}
                                <div>
                                  <h4 style={{ margin: '0 0 0.5rem', color: '#e2e8f0' }}>Token</h4>
                                  {tokens.length ? (
                                    tokens.map((token) => {
                                      const secret = issuedSecrets[token.id];
                                      const qrValue = secret?.qrPayload ?? token.qrPayload ?? '';
                                      return (
                                        <div
                                          key={token.id}
                                          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                                        >
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            <span className="badge badge-neutral">
                                              Prefix&nbsp;{token.tokenPrefix}
                                            </span>
                                            <span className="badge badge-neutral">
                                              Issued&nbsp;{new Date(token.createdAt).toLocaleString()}
                                            </span>
                                            <span className="badge badge-neutral">
                                              {token.expiresAt
                                                ? `Expires ${new Date(token.expiresAt).toLocaleString()}`
                                                : 'Never expires'}
                                            </span>
                                            <span className="badge badge-neutral">
                                              {token.revokedAt
                                                ? `Revoked ${new Date(token.revokedAt).toLocaleString()}`
                                                : 'Active'}
                                            </span>
                                          </div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {!token.revokedAt ? (
                                              <>
                                                <button type="button" onClick={() => void handleRevokeToken(token)}>
                                                  Revoke
                                                </button>
                                                <button type="button" onClick={() => void handleExtendToken(token)}>
                                                  Extend by 1 year
                                                </button>
                                              </>
                                            ) : null}
                                            {secret ? (
                                              <button type="button" onClick={() => void handleCopy(secret.token)}>
                                                Copy Secret
                                              </button>
                                            ) : null}
                                            {qrValue ? (
                                              <button
                                                type="button"
                                                onClick={() => handleDownloadQr(token.id, device.label, token.tokenPrefix)}
                                              >
                                                Download PNG
                                              </button>
                                            ) : null}
                                          </div>
                                          {qrValue ? (
                                            <div className="admin-qr-wrapper">
                                              <QRCodeSVG value={qrValue} size={132} />
                                              {secret ? (
                                                <div>
                                                  <p>Token</p>
                                                  <code>{secret.token}</code>
                                                </div>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <p style={{ color: '#94a3b8' }}>No token available.</p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No devices registered yet.</p>
          )
        ) : null}
      </section>
    </div>
  );
}
